import React, { useEffect, useState } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { APP_CONFIG } from "./authConfig";

function App() {
  const { oktaAuth, authState } = useOktaAuth();

  const [status, setStatus] = useState("Checking your account...");
  const [attempt, setAttempt] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!authState) {
      return;
    }

    // Not authenticated -> Okta
    if (!authState.isAuthenticated) {
      oktaAuth.signInWithRedirect({
        originalUri: "/"
      });

      return;
    }

    let cancelled = false;
    let countdownTimer = null;

    const sleep = (milliseconds) =>
      new Promise((resolve) => setTimeout(resolve, milliseconds));

    const startCountdown = async () => {
      setSecondsLeft(10);

      countdownTimer = setInterval(() => {
        setSecondsLeft((previous) =>
          previous > 0 ? previous - 1 : 0
        );
      }, 1000);

      await sleep(10000);

      clearInterval(countdownTimer);
      countdownTimer = null;

      setSecondsLeft(0);
    };

    const checkOktaGroup = async () => {
      try {
        // Get authenticated Okta user's details
        const user = await oktaAuth.getUser();

        if (cancelled) return;

        console.log("Logged-in Okta user:", user);

        /*
         * Call YOUR backend.
         *
         * Backend will:
         * 1. Receive user's email
         * 2. Find user in Okta
         * 3. Check required Okta group
         * 4. Return:
         *
         * {
         *   "isMember": true
         * }
         */

        const response = await fetch(
          APP_CONFIG.groupCheckApi,
          {
            method: "POST",

            headers: {
              "Content-Type": "application/json"
            },

            body: JSON.stringify({
              email: user.email
            })
          }
        );

        if (!response.ok) {
          throw new Error(
            `Group check failed: ${response.status}`
          );
        }

        const result = await response.json();

        return result.isMember === true;

      } catch (error) {
        console.error(
          "Error checking Okta group:",
          error
        );

        return false;
      }
    };

    const verifyAccess = async () => {
      // -----------------------------------
      // ATTEMPT 1 - Immediately
      // -----------------------------------

      setAttempt(1);

      setStatus(
        "Checking your SharePoint access..."
      );

      let isMember = await checkOktaGroup();

      if (cancelled) return;

      if (isMember) {
        setStatus(
          "Access confirmed. Redirecting to SharePoint..."
        );

        window.location.replace(
          APP_CONFIG.sharePointUrl
        );

        return;
      }


      // -----------------------------------
      // WAIT 10 SECONDS
      // -----------------------------------

      setStatus(
        "Your account is getting ready. We will check again shortly..."
      );

      await startCountdown();

      if (cancelled) return;


      // -----------------------------------
      // ATTEMPT 2
      // -----------------------------------

      setAttempt(2);

      setStatus(
        "Checking your account again..."
      );

      isMember = await checkOktaGroup();

      if (cancelled) return;

      if (isMember) {
        setStatus(
          "Access confirmed. Redirecting to SharePoint..."
        );

        window.location.replace(
          APP_CONFIG.sharePointUrl
        );

        return;
      }


      // -----------------------------------
      // WAIT ANOTHER 10 SECONDS
      // -----------------------------------

      setStatus(
        "Your account is still being prepared. Please wait..."
      );

      await startCountdown();

      if (cancelled) return;


      // -----------------------------------
      // ATTEMPT 3
      // -----------------------------------

      setAttempt(3);

      setStatus(
        "Performing final access check..."
      );

      isMember = await checkOktaGroup();

      if (cancelled) return;

      if (isMember) {
        setStatus(
          "Access confirmed. Redirecting to SharePoint..."
        );

        window.location.replace(
          APP_CONFIG.sharePointUrl
        );

        return;
      }


      // -----------------------------------
      // STILL NOT READY
      // -----------------------------------

      setFailed(true);

      setStatus(
        "Your account is taking longer than expected to prepare."
      );
    };

    verifyAccess();

    return () => {
      cancelled = true;

      if (countdownTimer) {
        clearInterval(countdownTimer);
      }
    };

  }, [authState, oktaAuth]);


  // ---------------------------------------
  // OKTA AUTH STATE LOADING
  // ---------------------------------------

  if (!authState) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>

          <div style={styles.spinner}></div>

          <h2>Checking authentication...</h2>

          <p>
            Please wait while we verify your account.
          </p>

        </div>
      </div>
    );
  }


  // ---------------------------------------
  // REDIRECTING TO OKTA
  // ---------------------------------------

  if (!authState.isAuthenticated) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>

          <div style={styles.spinner}></div>

          <h2>Redirecting to Sign In...</h2>

          <p>
            Please wait while we redirect you securely.
          </p>

        </div>
      </div>
    );
  }


  // ---------------------------------------
  // ACCOUNT PREPARATION
  // ---------------------------------------

  return (
    <div style={styles.container}>

      <div style={styles.card}>

        {!failed && (
          <div style={styles.spinner}></div>
        )}

        <h2>
          {failed
            ? "Account preparation is taking longer"
            : "Your account is getting ready"}
        </h2>

        <p>
          {status}
        </p>

        {!failed && (
          <>
            <div style={styles.statusBox}>
              Preparing your SharePoint access...
            </div>

            <p style={styles.attempt}>
              Verification attempt: {attempt} of 3
            </p>

            {secondsLeft > 0 && (
              <p style={styles.timer}>
                Checking again in {secondsLeft} seconds
              </p>
            )}

            <p style={styles.smallText}>
              Please do not close or refresh this window.
            </p>
          </>
        )}

        {failed && (
          <>
            <div style={styles.errorBox}>
              Your SharePoint access is not ready yet.
            </div>

            <p style={styles.smallText}>
              Please try again after a few moments.
            </p>

            <button
              style={styles.button}
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
          </>
        )}

      </div>

    </div>
  );
}


const styles = {

  container: {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#f5f5f5",
    fontFamily: "Arial, sans-serif"
  },

  card: {
    width: "450px",
    padding: "40px",
    backgroundColor: "#ffffff",
    borderRadius: "10px",
    textAlign: "center",
    boxShadow: "0 4px 15px rgba(0,0,0,0.1)"
  },

  spinner: {
    width: "45px",
    height: "45px",
    margin: "0 auto 25px",
    border: "5px solid #e5e5e5",
    borderTop: "5px solid #333",
    borderRadius: "50%",
    animation: "spin 1s linear infinite"
  },

  statusBox: {
    marginTop: "25px",
    padding: "15px",
    backgroundColor: "#f1f1f1",
    borderRadius: "6px",
    fontWeight: "bold"
  },

  errorBox: {
    marginTop: "25px",
    padding: "15px",
    backgroundColor: "#f1f1f1",
    borderRadius: "6px",
    fontWeight: "bold"
  },

  attempt: {
    marginTop: "20px",
    fontSize: "14px"
  },

  timer: {
    marginTop: "15px",
    fontSize: "18px",
    fontWeight: "bold"
  },

  smallText: {
    marginTop: "15px",
    fontSize: "13px"
  },

  button: {
    marginTop: "20px",
    padding: "10px 25px",
    cursor: "pointer"
  }
};

export default App;